import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const links = [
  { to: "/", label: "Home" },
  { to: "/books", label: "Books" },
  { to: "/characters", label: "Characters" },
  { to: "/ella-raine", label: "Ella Raine" },
  { to: "/about", label: "About" },
  { to: "/news", label: "News" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-[#1A1D24]/85 backdrop-blur-xl ${
          scrolled
            ? "border-b border-[#3A4757] shadow-[0_4px_24px_rgba(0,0,0,0.4)]"
            : "border-b border-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <Link
            to="/"
            data-testid="nav-logo"
            className="font-serif text-2xl tracking-wide text-[#F4F4F6] hover:text-[#D4AF37] transition-colors duration-300"
          >
            Ellie<span className="text-[#D4AF37]">.</span>Stone
          </Link>

          <ul className="hidden md:flex items-center gap-10">
            {links.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `text-sm tracking-wide transition-colors duration-300 ${
                      isActive
                        ? "text-[#D4AF37]"
                        : "text-[#F4F4F6]/70 hover:text-[#F4F4F6]"
                    }`
                  }
                  end={l.to === "/"}
                >
                  {l.label}
                </NavLink>
              </li>
            ))}
          </ul>

          <Link
            to="/books"
            data-testid="nav-cta-shop"
            className="hidden md:inline-flex btn-primary"
          >
            Read Now
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(true)}
            className="md:hidden text-[#F4F4F6] p-2"
            aria-label="Open menu"
          >
            <Menu size={24} />
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-[#1A1D24]/98 backdrop-blur-2xl flex flex-col"
          >
            <div className="flex justify-end p-6">
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="text-[#F4F4F6] p-2"
                aria-label="Close menu"
              >
                <X size={28} />
              </button>
            </div>
            <ul className="flex-1 flex flex-col items-center justify-center gap-8">
              {links.map((l, i) => (
                <motion.li
                  key={l.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.08 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    className="font-serif text-4xl text-[#F4F4F6] hover:text-[#D4AF37] transition-colors"
                  >
                    {l.label}
                  </NavLink>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
